import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Plus, Search, Filter, CheckCircle2 } from "lucide-react";
import VehiclePhoto from "@/components/app/VehiclePhoto";
import MarkSoldModal from "@/components/app/MarkSoldModal";

function nzd(n) { return new Intl.NumberFormat("en-NZ", { style: "currency", currency: "NZD", maximumFractionDigits: 0 }).format(n || 0); }

function ageDays(v) {
  const d = new Date(v.date_acquired || v.created_at);
  return Math.max(0, Math.floor((Date.now() - d.getTime()) / 86400000));
}
function agingClass(days) {
  if (days <= 30) return "aging-fresh";
  if (days <= 60) return "aging-moderate";
  if (days <= 90) return "aging-aging";
  return "aging-critical";
}

const STATUS_LABEL = { available: "Available", reserved: "Reserved", in_prep: "In Prep", sold: "SOLD" };
const STATUS_COLOR = {
  available: "bg-emerald-50 text-emerald-700 border-emerald-200",
  reserved: "bg-blue-50 text-blue-700 border-blue-200",
  in_prep: "bg-amber-50 text-amber-700 border-amber-200",
  sold: "bg-rose-600 text-white border-rose-700",
};

export default function Vehicles() {
  const [params] = useSearchParams();
  const [vehicles, setVehicles] = useState([]);
  const [search, setSearch] = useState(params.get("q") || "");
  const [status, setStatus] = useState("all");
  const [sellTarget, setSellTarget] = useState(null);

  const load = async () => {
    const { data } = await api.get("/vehicles", { params: { search: search || undefined, status } });
    setVehicles(data);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [search]);

  const totalValue = useMemo(() => vehicles.filter((v) => v.status !== "sold").reduce((s, v) => s + (v.list_price || 0), 0), [vehicles]);
  const availableCount = useMemo(() => vehicles.filter((v) => v.status !== "sold").length, [vehicles]);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>Vehicle Inventory</h1>
          <p className="text-sm text-slate-500 mt-1">{availableCount} in stock · {nzd(totalValue)} listed value</p>
        </div>
        <Link to="/vehicles/new" data-testid="vehicle-add-button" className="h-10 px-4 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg flex items-center gap-2">
          <Plus className="w-4 h-4" /> Add Vehicle
        </Link>
      </div>

      <div className="card-elevated p-4 flex flex-col md:flex-row gap-3">
        <div className="flex-1 relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="vehicle-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search VIN, Rego, Stock #, make or model…"
            className="w-full h-10 pl-9 pr-4 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-slate-400" />
          <select
            data-testid="vehicle-status-filter"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="h-10 px-3 border border-slate-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All statuses</option>
            <option value="available">Available</option>
            <option value="reserved">Reserved</option>
            <option value="in_prep">In Prep</option>
            <option value="sold">Sold</option>
          </select>
        </div>
      </div>

      <div className="card-elevated overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[11px] uppercase tracking-widest text-slate-500 bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left py-3 pl-4">Photo</th>
                <th className="text-left">Stock #</th>
                <th className="text-left">Vehicle</th>
                <th className="text-left">Rego / VIN</th>
                <th className="text-right">Cost</th>
                <th className="text-right">List Price</th>
                <th className="text-right">Margin</th>
                <th className="text-center">Age</th>
                <th className="text-left">Status</th>
                <th className="pr-4"></th>
              </tr>
            </thead>
            <tbody>
              {vehicles.length === 0 && (
                <tr><td colSpan={10} className="py-16 text-center text-slate-400">No vehicles match your filters.</td></tr>
              )}
              {vehicles.map((v) => {
                const cost = (v.purchase_cost || 0) + (v.reconditioning_cost || 0) + (v.transport_cost || 0);
                const margin = (v.list_price || 0) - cost;
                const age = ageDays(v);
                const isSold = v.status === "sold";
                return (
                  <tr
                    key={v.id}
                    data-testid={`vehicle-table-row-${v.stock_no}`}
                    className={`border-b border-slate-100 ${isSold ? "bg-rose-50/50 hover:bg-rose-50" : "row-hover"}`}
                  >
                    <td className="py-3 pl-4">
                      <div className={`w-16 h-11 bg-slate-100 rounded-md overflow-hidden ${isSold ? "opacity-70" : ""}`}>
                        <VehiclePhoto vehicle={v} className="w-full h-full object-cover" />
                      </div>
                    </td>
                    <td className={`font-mono text-xs ${isSold ? "text-rose-700 font-semibold" : "text-slate-700"}`}>{v.stock_no}</td>
                    <td>
                      <div className={`font-semibold ${isSold ? "text-rose-800 line-through decoration-rose-400 decoration-1" : "text-slate-900"}`}>{v.year} {v.make} {v.model}</div>
                      <div className="text-xs text-slate-500">{v.body_type} · {v.transmission} · {v.fuel_type}</div>
                    </td>
                    <td className="font-mono text-xs">
                      <div className={`font-semibold ${isSold ? "text-rose-700" : "text-slate-900"}`}>{v.rego || "—"}</div>
                      <div className="text-slate-400">{(v.vin || "").slice(0, 12)}{v.vin?.length > 12 ? "…" : ""}</div>
                    </td>
                    <td className="text-right font-mono text-xs">{nzd(cost)}</td>
                    <td className={`text-right font-mono text-xs font-semibold ${isSold ? "text-rose-700" : "text-slate-900"}`}>{nzd(v.list_price)}</td>
                    <td className={`text-right font-mono text-xs font-semibold ${margin >= 0 ? (isSold ? "text-emerald-700" : "text-emerald-700") : "text-rose-700"}`}>{nzd(margin)}</td>
                    <td className="text-center">
                      {isSold ? (
                        <span className="text-[10px] font-mono font-semibold text-rose-600">—</span>
                      ) : (
                        <span className={`inline-block text-[10px] font-mono font-semibold px-2 py-1 rounded ${agingClass(age)}`}>{age}d</span>
                      )}
                    </td>
                    <td>
                      <span className={`inline-block text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded border ${STATUS_COLOR[v.status]}`}>{STATUS_LABEL[v.status]}</span>
                    </td>
                    <td className="pr-4 text-right whitespace-nowrap">
                      {!isSold && (
                        <button
                          onClick={() => setSellTarget(v)}
                          data-testid={`vehicle-mark-sold-${v.stock_no}`}
                          className="text-xs font-semibold text-emerald-700 hover:text-emerald-800 hover:bg-emerald-50 px-2 py-1 rounded inline-flex items-center gap-1 mr-1"
                        >
                          <CheckCircle2 className="w-3.5 h-3.5" /> Mark Sold
                        </button>
                      )}
                      <Link to={`/vehicles/${v.id}`} data-testid={`vehicle-edit-${v.stock_no}`} className="text-xs font-semibold text-blue-600 hover:text-blue-700">Edit →</Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {sellTarget && (
        <MarkSoldModal vehicle={sellTarget} onClose={() => setSellTarget(null)} onDone={load} />
      )}
    </div>
  );
}
